import * as React from 'react';
import { useCallback, useEffect, useMemo, useReducer } from 'react';

import { throttle } from 'lodash';

import { breakpoints, useTheme } from '@coursera/cds-core';

import type { NavigationLink } from 'bundles/unified-description-page-common/components/shared/PageNavigation';
import PageNavigationContext from 'bundles/unified-description-page-common/contexts/PageNavigationContext';
import type { PageNavigationContextType } from 'bundles/unified-description-page-common/contexts/PageNavigationContext';

export const initialState = {
  showFixedPageNav: false,
  showPageCtaInNav: false,
  showFixedMobileCta: false,
  navigationLinkKeys: [],
};
const SCROLL_MARGIN = 180;

export const ACTION_TYPES = {
  UPDATE_IN_PAGE_NAV_POSITION: 'updateInPageNavPosition',
  UPDATE_PAGE_CTA_POSITION: 'updatePageCtaPosition',
  UPDATE_SECTION_IN_VIEW: 'updateSectionInView',
  ADD_SECTION_ELEMENT_REF: 'addSectionElementRef',
  UPDATE_FIXED_PAGE_NAV_HEIGHT: 'updateFixedPageNavHeight',
  UPDATE_NAVIGATION_LINK_KEYS: 'updateNavigationLinkKeys',
} as const;

type UpdateInPageNavPositionAction = {
  type: typeof ACTION_TYPES.UPDATE_IN_PAGE_NAV_POSITION;
  payload: {
    top: number;
    isScrollingDown?: boolean;
  };
};

type UpdatePageCtaPositionAction = {
  type: typeof ACTION_TYPES.UPDATE_PAGE_CTA_POSITION;
  payload: {
    bottom: number;
    mobileBreakpoint: number;
  };
};

type UpdateSectionInViewAction = {
  type: typeof ACTION_TYPES.UPDATE_SECTION_IN_VIEW;
};

type UpdateNavigationLinkKeysAction = {
  type: typeof ACTION_TYPES.UPDATE_NAVIGATION_LINK_KEYS;
  payload: {
    navLinkKeys: string[];
  };
};
type addSectionElementRefAction = {
  type: typeof ACTION_TYPES.ADD_SECTION_ELEMENT_REF;
  payload: {
    anchorId: string;
    sectionRef: HTMLDivElement;
  };
};

type UpdateFixedPageNavHeightAction = {
  type: typeof ACTION_TYPES.UPDATE_FIXED_PAGE_NAV_HEIGHT;
  payload: {
    height: number;
  };
};

type Action =
  | UpdateInPageNavPositionAction
  | UpdatePageCtaPositionAction
  | addSectionElementRefAction
  | UpdateFixedPageNavHeightAction
  | UpdateNavigationLinkKeysAction
  | UpdateSectionInViewAction;

export function reducer(state: PageNavigationContextType, action: Action) {
  switch (action.type) {
    case ACTION_TYPES.UPDATE_IN_PAGE_NAV_POSITION: {
      const { top, isScrollingDown } = action.payload;
      /**
       * On desktop, once the user scrolls down past the in-page navigation, a fixed header appears.
       * On tablet/mobile, after the user scrolls down past the key information module,
       * if they keep scrolling down, the fixed header remains hidden;
       * once they start scrolling up, the fixed header appears.
       */
      if (top <= 0) {
        if (isScrollingDown === true) {
          return state.showFixedPageNav === false
            ? state
            : {
                ...state,
                showFixedPageNav: false,
              };
        }

        return state.showFixedPageNav === true
          ? state
          : {
              ...state,
              showFixedPageNav: true,
            };
      }

      return state.showFixedPageNav === false
        ? state
        : {
            ...state,
            showFixedPageNav: false,
          };
    }
    case ACTION_TYPES.UPDATE_PAGE_CTA_POSITION: {
      const { bottom, mobileBreakpoint } = action.payload;
      const CTA_HEIGHT = 65;

      if (state.fixedPageNavHeight && bottom <= state.fixedPageNavHeight && state.showPageCtaInNav === false) {
        return {
          ...state,
          showPageCtaInNav: true,
        };
      } else if (state.fixedPageNavHeight && bottom > state.fixedPageNavHeight && state.showPageCtaInNav === true) {
        return {
          ...state,
          showPageCtaInNav: false,
        };
      }

      if (window.innerWidth <= mobileBreakpoint) {
        if (bottom <= CTA_HEIGHT && state.showFixedMobileCta === false) {
          return {
            ...state,
            showFixedMobileCta: true,
          };
        } else if (bottom > CTA_HEIGHT && state.showFixedMobileCta === true) {
          return {
            ...state,
            showFixedMobileCta: false,
          };
        }
      } else if (window.innerWidth > mobileBreakpoint && state.showFixedMobileCta === true) {
        return {
          ...state,
          showFixedMobileCta: false,
        };
      }

      return state;
    }
    case ACTION_TYPES.UPDATE_FIXED_PAGE_NAV_HEIGHT: {
      const { height } = action.payload;

      if (height !== state.fixedPageNavHeight) {
        return {
          ...state,
          fixedPageNavHeight: height,
        };
      }
      return state;
    }
    case ACTION_TYPES.ADD_SECTION_ELEMENT_REF: {
      const { anchorId, sectionRef } = action.payload;
      return {
        ...state,
        sectionRefs: {
          ...state.sectionRefs,
          [anchorId]: sectionRef,
        },
      };
    }
    case ACTION_TYPES.UPDATE_NAVIGATION_LINK_KEYS: {
      const { navLinkKeys } = action.payload;
      const { navigationLinkKeys } = state;
      if (
        navigationLinkKeys.length !== navLinkKeys.length ||
        navigationLinkKeys.find((key, index) => key !== navLinkKeys[index])
      ) {
        return {
          ...state,
          navigationLinkKeys: navLinkKeys,
        };
      }
      return state;
    }
    case ACTION_TYPES.UPDATE_SECTION_IN_VIEW: {
      if (typeof window === 'undefined') {
        return state;
      }

      let newState = state;
      const { sectionRefs, navigationLinkKeys } = state;
      const { scrollY } = window;

      // Iterate through all of the section elements and figure out which section is currently in view
      // based on scroll position.
      const activeStates = navigationLinkKeys.map((key, index) => {
        const currentSection = sectionRefs?.[navigationLinkKeys[index]];
        const nextSection = navigationLinkKeys[index + 1] && sectionRefs?.[navigationLinkKeys[index + 1]];

        if (!currentSection) {
          return false;
        }

        const { offsetTop } = currentSection;
        const { offsetTop: nextSectionOffsetTop } = nextSection || {};
        const isLastSection = index === navigationLinkKeys.length - 1;
        const isScrollPastElement = scrollY >= offsetTop - SCROLL_MARGIN;
        const isScrollPastNextSection =
          !isLastSection && nextSectionOffsetTop ? scrollY >= nextSectionOffsetTop - SCROLL_MARGIN : false;
        const isSectionActive = isScrollPastElement && !isScrollPastNextSection;
        const isScrollPastLastElelment =
          isLastSection && scrollY >= offsetTop + currentSection.offsetHeight - SCROLL_MARGIN;

        // when scroll past the last section, no section is in view anymore.
        if (isScrollPastLastElelment) {
          newState = {
            ...newState,
            sectionInView: undefined,
          };
        } else if (isSectionActive) {
          newState = {
            ...newState,
            sectionInView: key,
          };
        }

        return isSectionActive;
      });

      // If no sections are "in view" (for example, at the top of the page we are technically not "at" the
      // first section yet), default to making the first section active.
      if (!activeStates.some((isSectionActive) => isSectionActive)) {
        newState = {
          ...newState,
          sectionInView: navigationLinkKeys[0],
        };
      }

      return newState;
    }
    default:
      return state;
  }
}

const PageNavigationProvider = ({
  navigationLinks,
  children,
}: {
  navigationLinks: Array<NavigationLink>;
  children: React.ReactNode;
}) => {
  const theme = useTheme();
  const navigationLinkKeys = navigationLinks.map((link) => link.name);

  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    navigationLinkKeys,
    sectionInView: navigationLinkKeys[0],
  });

  const updateInPageNavPosition = useCallback((top, isScrollingDown) => {
    dispatch({
      type: ACTION_TYPES.UPDATE_IN_PAGE_NAV_POSITION,
      payload: {
        top,
        isScrollingDown,
      },
    });
  }, []);

  const updateSectionInView = useCallback(() => {
    dispatch({
      type: ACTION_TYPES.UPDATE_SECTION_IN_VIEW,
    });
  }, []);

  const updateNavigationLinkKeys = useCallback((navLinkKeys) => {
    dispatch({
      type: ACTION_TYPES.UPDATE_NAVIGATION_LINK_KEYS,
      payload: {
        navLinkKeys,
      },
    });
  }, []);

  const updatePageCtaPosition = useCallback((bottom) => {
    dispatch({
      type: ACTION_TYPES.UPDATE_PAGE_CTA_POSITION,
      payload: {
        bottom,
        mobileBreakpoint: breakpoints.values.md - 1,
      },
    });
  }, []);

  const updateFixedPageNavHeight = useCallback((height) => {
    dispatch({
      type: ACTION_TYPES.UPDATE_FIXED_PAGE_NAV_HEIGHT,
      payload: {
        height,
      },
    });
  }, []);

  const addSectionElementRef = useCallback((anchorId, sectionRef) => {
    dispatch({
      type: ACTION_TYPES.ADD_SECTION_ELEMENT_REF,
      payload: {
        anchorId,
        sectionRef,
      },
    });
  }, []);

  useEffect(() => {
    const throttledUpdateSectionInView = throttle(() => {
      updateSectionInView();
    }, 100);

    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', throttledUpdateSectionInView);
      window.addEventListener('resize', throttledUpdateSectionInView);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('scroll', throttledUpdateSectionInView);
        window.removeEventListener('resize', throttledUpdateSectionInView);
      }
    };
  }, [updateSectionInView]);

  useEffect(() => {
    updateNavigationLinkKeys(navigationLinks.map((link) => link.name));
  }, [navigationLinks, updateNavigationLinkKeys]);

  const value = useMemo(
    () => ({
      ...state,
      updateInPageNavPosition,
      updatePageCtaPosition,
      updateFixedPageNavHeight,
      addSectionElementRef,
      updateSectionInView,
      navigationLinks,
    }),
    [
      state,
      updateInPageNavPosition,
      updatePageCtaPosition,
      updateFixedPageNavHeight,
      addSectionElementRef,
      updateSectionInView,
      navigationLinks,
    ]
  );

  return <PageNavigationContext.Provider value={value}>{children}</PageNavigationContext.Provider>;
};

export default PageNavigationProvider;
